import { useEffect, useRef } from 'react'
import { getActiveProductFromPath } from '../utils/getProduct'
import trackHelpScoutBeaconOpened from '../../tracking/HelpScoutBeacon/trackHelpScoutBeaconOpened'
import trackHelpScoutBeaconClosed from '../../tracking/HelpScoutBeacon/trackHelpScoutBeaconClosed'
import trackHelpScoutBeaconArticleViewed from '../../tracking/HelpScoutBeacon/trackHelpScoutBeaconArticleViewed'
import trackHelpScoutBeaconEmailSent from '../../tracking/HelpScoutBeacon/trackHelpScoutBeaconEmailSent'
import trackHelpScoutBeaconSearchQueryPerformed from '../../tracking/HelpScoutBeacon/trackHelpScoutBeaconSearchQueryPerformed'
import trackHelpScoutBeaconChatStarted from '../../tracking/HelpScoutBeacon/trackHelpScoutBeaconChatStarted'
const activeProduct = getActiveProductFromPath()
import { env } from '../../env'

interface BeaconInHelpMenu {
  isReady: boolean
  isEnabled: boolean
}

interface BeaconWidget {
  isReady: boolean
  isEnabled: boolean
}

export function getHorizontalPosition(activeProduct: string): number {
  switch (activeProduct) {
    case 'engage':
      return 63
    case 'start_page':
      return 80
    default:
      return 40
  }
}

function positionBeacon(activeProduct: string): void {
  if (window.Beacon) {
    window.Beacon('config', {
      display: {
        horizontalOffset: getHorizontalPosition(activeProduct),
      },
    })
  }
}

/* eslint-disable */
export function configureBeacon(activeProduct: string, user: any): void {
  if (window.Beacon) {
    // Don't reconfigure while Beacon is open — it resets the contact form
    if (isBeaconOpen()) return

    window.Beacon('config', {
      display: {
        style: 'manual',
        horizontalOffset: getHorizontalPosition(activeProduct),
        zIndex: 9999,
      },
      messaging: {
        chatEnabled: false,
      },
      docsEnabled: false,
      aiAnswersEnabled: false,
      labels: {
        messageConfirmationText: user.email
          ? `We'll email a response to ${user.email}, typically within a few hours.`
          : `We'll email you a response soon, typically within a few hours.`,
      },
    })

    window.Beacon('prefill', {
      name: user.name ? user.name : '',
    })
  }
}

export function openBeacon(): void {
  window.Beacon?.('open')
}

export function closeBeacon(): void {
  // 'close' is a valid Beacon command but not in the type definitions
  ;(window.Beacon as (command: string) => void)?.('close')
}

export function isBeaconOpen(): boolean {
  return !!window.Beacon?.('info')?.status?.isOpened
}

function identifyUser(user: any): void {
  if (window.Beacon && !user.loading) {
    const sixtyDays = 60 * 24 * 60 * 60 * 1000
    const joinedMoreThan60DaysAgo = user.createdAt
      ? new Date().getTime() - new Date(user.createdAt).getTime() > sixtyDays
      : false
    window.Beacon('identify', {
      email: user.email,
      signature: user.helpScoutBeaconSignature,
      'account-id': user.id,
      'joined-60-days-ago': joinedMoreThan60DaysAgo,
    })
  }
}

function closeBrainfishWidget(): void {
  if (window.Brainfish?.Widgets?.close) {
    window.Brainfish.Widgets.close()
  }
}

function setupEventListeners(
  user: any,
  onVisibilityChange?: (hidden: boolean) => void,
): void {
  if (window.Beacon) {
    window.Beacon('on', 'open', () => {
      onVisibilityChange?.(true)
      trackHelpScoutBeaconOpened({ user })
    })

    window.Beacon('on', 'close', () => {
      // Hide Beacon's native icon (Brainfish provides the help button)
      window.Beacon?.('config', { display: { style: 'manual' } })
      onVisibilityChange?.(false)
      window.Beacon?.('reset')
      window.Beacon?.('config', {
        display: { style: 'manual' },
        messaging: {
          contactForm: {
            customFieldsEnabled: true,
          },
        },
      })
      trackHelpScoutBeaconClosed({ user })
    })

    window.Beacon(
      'on',
      'article-viewed',
      ({ id: articleId }: { id: string }) => {
        trackHelpScoutBeaconArticleViewed({ user, articleId })
      },
    )

    window.Beacon('on', 'search', ({ query }: { query: string }) => {
      trackHelpScoutBeaconSearchQueryPerformed({ user, searchTerm: query })
    })

    window.Beacon('on', 'email-sent', () => {
      const mailboxId = window.Beacon?.('info')?.config?.messaging?.mailboxId
      trackHelpScoutBeaconEmailSent({
        user,
        mailboxId: mailboxId != null ? String(mailboxId) : undefined,
      })
    })

    window.Beacon('on', 'chat-started', ({ subject }: { subject: string }) => {
      trackHelpScoutBeaconChatStarted({ user, subject })
    })

    // NPS surveys and other proactive messages — hide Brainfish so the
    // HelpScout message is visible, and signal that Beacon UI is showing.
    // The sentinel prevents useBrainfishWidget's handleBrainfishClose from
    // calling onVisibilityChange(false) when it detects the Brainfish close
    // event that we trigger here.
    window.Beacon('on', 'message-triggered', () => {
      window.__beaconMessageActive = true
      closeBrainfishWidget()
      onVisibilityChange?.(true)
    })

    window.Beacon('on', 'message-closed', () => {
      window.__beaconMessageActive = false
      if (!isBeaconOpen()) {
        onVisibilityChange?.(false)
      }
    })
  }
}

// prettier-ignore
/* eslint-disable */
export default function useHelpScoutBeacon(
  user: any,
  showBeaconInHelpMenu: BeaconInHelpMenu,
  loadBeaconWidget: BeaconWidget,
  onVisibilityChange?: (hidden: boolean) => void,
): void {
  const isBeaconInitialized = useRef(false)

  useEffect(() => {
    // Ensuring Beacon is loaded and user is not in loading state
    const initializeBeacon = () => {
      if (window.Beacon && !user.loading && !isBeaconInitialized.current) {
        window.Beacon?.('reset')
        // Immediately hide native button to prevent flash before configureBeacon runs
        window.Beacon('config', { display: { style: 'manual' } })
        identifyUser(user)
        positionBeacon(activeProduct)
        setupEventListeners(user, onVisibilityChange)
        isBeaconInitialized.current = true
        setTimeout(() => configureBeacon(activeProduct, user), 1000)
      }
    }

    if (!window.Beacon) {
      if (
        showBeaconInHelpMenu.isReady &&
        loadBeaconWidget.isReady &&
        loadBeaconWidget.isEnabled
      ) {
        // prettier-ignore
        // @ts-ignore — vendored HelpScout loader snippet; must stay on one line for ts-ignore to cover it
        !function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});

        // @ts-expect-error
        window.Beacon('init', env.HELP_SCOUT_BEACON_ID)

        // @ts-expect-error
        window.Beacon('on', 'ready', initializeBeacon)
      }
    } else {
      // Initialize immediately if Beacon is already present
      initializeBeacon()
      identifyUser(user)
      positionBeacon(activeProduct)
      setTimeout(() => configureBeacon(activeProduct, user), 1000)
    }
  }, [
    user,
    activeProduct,
    showBeaconInHelpMenu,
    loadBeaconWidget,
    onVisibilityChange,
  ])
}
